/**
 * Your account, rebuilt on the template's own account-settings pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx
 *   the display-name form as its own card with a footer bar;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/password-settings.tsx
 *   current / new / confirm-new password, each a rizzui `Password` with its own show-hide toggle;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/logged-in-devices/columns.tsx
 *   the signed-in device rows and the "active now" dot badge;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `components/detail-parts.tsx`'s `PageHero`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   the identity card's icon-tile label/value blocks (`InfoBlock`).
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState, type ReactNode } from "react";
import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiBuildingsDuotone as CompanyIcon,
  PiCheckCircleDuotone as SavedIcon,
  PiDevicesDuotone as DevicesIcon,
  PiEnvelopeSimpleDuotone as EmailIcon,
  PiIdentificationBadgeDuotone as DisplayNameIcon,
  PiInfoDuotone as InfoIcon,
  PiLockKeyDuotone as PasswordIcon,
  PiShieldCheckDuotone as RoleIcon,
  PiUserCircleDuotone as IdentityIcon,
} from "react-icons/pi";
import { Alert, Avatar, Badge, Button, Password, Text } from "rizzui";
import { EmptyState, ErrorSurface, Field, WidgetCard, cn } from "@steelyard/theme";
import { changeDisplayName, changePassword } from "../../lib/services/account";
import { getAccount, listMySessions, revokeMySession, type MySession } from "../../lib/identity";
import { formatMoment, roleName } from "../../lib/format";
import { CardTitle, CopyButton, InfoBlock, PageHero } from "../../components/detail-parts";
import type { AccountSummary, PermissionState } from "../../types";

/** Loaded alongside the root's account/permissionState (route-scoped, not the shared root loader). */
export async function sessionsLoader() {
  const [sessions, account] = await Promise.all([listMySessions(), getAccount()]);
  // Identity sends the company's name beside its reference; the reference itself is never shown.
  return { sessions, companyName: account?.companyName ?? null };
}

/** Shown, never compared — every gate goes through lib/permissions.ts. */
function roleLabel(role: string) {
  return roleName(role);
}

/**
 * Ported from apps/admin/app/(app)/account/{page,actions,error}.tsx. Two independent forms, each
 * stating its own outcome explicitly (constitution VI) — "it saved" and "nothing happened" must
 * not look identical. The Next version's `?changed=` query param becomes local per-form state;
 * the per-page `error.tsx` boundary becomes a per-form caught-and-rendered ErrorSurface, since a
 * plain onSubmit handler's thrown error doesn't reach a route's errorElement.
 *
 * Nothing here is permission-gated: every call acts on the caller's own account.
 */
export default function AccountPage() {
  const { account } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const { sessions, companyName } = useLoaderData() as Awaited<ReturnType<typeof sessionsLoader>>;
  const name = account.displayName ?? account.email ?? "Your account";

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Your Account" }]}
        icon={<Avatar name={name} color="secondary" customSize={64} rounded="lg" className="font-lexend text-2xl" />}
        title={name}
        description={
          account.email ? (
            <span className="inline-flex items-center gap-1">
              {account.email}
              <CopyButton value={account.email} label="Copy email" />
            </span>
          ) : (
            "No email on this account."
          )
        }
        meta={
          <>
            <Badge variant="flat" color="secondary" rounded="pill" className="gap-1.5 capitalize">
              <RoleIcon aria-hidden className="size-4" />
              {roleLabel(account.role)}
            </Badge>
            <Badge variant="flat" color="success" rounded="pill" className="gap-1.5">
              <DevicesIcon aria-hidden className="size-4" />
              {sessions.length} {sessions.length === 1 ? "signed-in device" : "signed-in devices"}
            </Badge>
            {account.companyRef ? (
              <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
                <CompanyIcon aria-hidden className="size-4" />
                Company account
              </Badge>
            ) : null}
          </>
        }
      />

      <div className="grid items-start gap-6 @5xl:grid-cols-12">
        <aside className="flex flex-col gap-6 @5xl:sticky @5xl:top-24 @5xl:col-span-4">
          <IdentityCard account={account} companyName={companyName} />
        </aside>

        <div className="flex flex-col gap-6 @5xl:col-span-8">
          <DisplayNameForm initial={account.displayName ?? ""} />
          {account.hasPassword !== false ? <PasswordForm /> : null}
          <SessionsSection sessions={sessions} />
        </div>
      </div>
    </div>
  );
}

function IdentityCard({ account, companyName }: { account: AccountSummary; companyName?: string | null }) {
  const sections = [
    { id: "account-profile", label: "Display name" },
    // A code-only account (warehouse staff) has no password to change.
    ...(account.hasPassword !== false ? [{ id: "account-password", label: "Password" }] : []),
    { id: "account-devices", label: "Signed-in devices" },
  ];

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<IdentityIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
          Who you are
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <div className="mt-5 flex flex-col gap-4">
        <InfoBlock icon={<EmailIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Email" action={account.email ? <CopyButton value={account.email} label="Copy email" /> : null}>
          <Text className="break-all text-sm font-medium text-gray-900">{account.email ?? "—"}</Text>
        </InfoBlock>
        <InfoBlock icon={<RoleIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Role">
          <Text className="text-sm font-medium text-gray-900">{roleLabel(account.role)}</Text>
        </InfoBlock>
        {account.companyRef ? (
          <InfoBlock icon={<CompanyIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Company">
            <Text className="break-all text-sm text-gray-900">{companyName ?? "Unknown customer"}</Text>
          </InfoBlock>
        ) : null}

        <Text className="flex items-start gap-2 rounded-lg bg-secondary-lighter/30 px-3 py-2.5 text-sm text-gray-700 dark:bg-secondary-lighter/10">
          <InfoIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-secondary" />
          Your role is set by an administrator, not here.
        </Text>

        <nav aria-label="Account sections" className="border-t border-dashed border-muted pt-4">
          <Text className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">On this page</Text>
          <ul className="flex flex-col gap-1">
            {sections.map((section) => (
              <li key={section.id}>
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  className="w-full justify-start px-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  {section.label}
                </Button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </WidgetCard>
  );
}

/** A form as its own card: icon-tile heading, outcome, fields, then a footer bar with the hint on the left and the buttons on the right. */
function FormCard({
  id,
  title,
  tile,
  icon,
  description,
  outcome,
  hint,
  children,
  footer,
  onSubmit,
}: {
  id: string;
  title: string;
  tile: string;
  icon: ReactNode;
  description: string;
  outcome: ReactNode;
  hint: string;
  children: ReactNode;
  footer: ReactNode;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}) {
  return (
    <WidgetCard
      rounded="xl"
      className="overflow-hidden p-0 lg:p-0"
      headerClassName="px-5 pb-0 pt-5 lg:px-7 lg:pt-6"
      title={
        <span id={id} className="scroll-mt-28">
          <CardTitle icon={icon} tile={tile}>
            {title}
          </CardTitle>
        </span>
      }
      description={<Text className="mt-2 text-sm text-gray-500">{description}</Text>}
    >
      <form onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-5 px-5 py-6 lg:px-7">
          {outcome}
          {children}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-muted bg-gray-50 px-5 py-4 lg:px-7 dark:bg-gray-100/40">
          <Text className="flex items-center gap-2 text-sm text-gray-500">
            <InfoIcon aria-hidden className="size-4 shrink-0" />
            {hint}
          </Text>
          <div className="flex items-center gap-2">{footer}</div>
        </div>
      </form>
    </WidgetCard>
  );
}

function Saved({ children }: { children: ReactNode }) {
  return (
    <div role="status">
      <Alert color="success" variant="flat" bar icon={<SavedIcon aria-hidden className="size-5" />}>
        <Text className="text-sm font-medium text-green-dark">{children}</Text>
      </Alert>
    </div>
  );
}

const displayNameSchema = z.object({ displayName: z.string().trim().min(1, "Name is required").max(200) });

function DisplayNameForm({ initial }: { initial: string }) {
  const revalidator = useRevalidator();
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof displayNameSchema>>({ resolver: zodResolver(displayNameSchema), defaultValues: { displayName: initial } });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    setSaved(false);
    try {
      await changeDisplayName(values.displayName);
      setSaved(true);
      toast.success("Display name changed");
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not change your display name");
    }
  });

  function handleAltBtn() {
    reset({ displayName: initial });
    setError(null);
    setSaved(false);
  }

  return (
    <FormCard
      id="account-profile"
      title="Display name"
      icon={<DisplayNameIcon aria-hidden className="size-5" />}
      tile="bg-blue-lighter text-blue-dark"
      description="Shown in the header and against anything you approve."
      hint="Colleagues see this name, not your email."
      onSubmit={onSubmit}
      outcome={
        <>
          {saved ? <Saved>Your display name has been changed.</Saved> : null}
          {error ? <ErrorSurface error={error} action="change your display name" /> : null}
        </>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={handleAltBtn}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Save name
          </Button>
        </>
      }
    >
      <Field
        label="Display name"
        required
        id="account-display-name"
        autoComplete="name"
        wrapperClassName="max-w-md"
        error={errors.displayName?.message}
        {...register("displayName")}
      />
    </FormCard>
  );
}

// `confirmNewPassword` is the template's own confirm field (password-settings.tsx). It never leaves
// this form — only current and new are sent, and the API still owns every password rule.
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z.string().min(1, "New password is required"),
    confirmNewPassword: z.string().min(1, "Confirm the new password"),
  })
  .refine((values) => values.newPassword === values.confirmNewPassword, {
    path: ["confirmNewPassword"],
    message: "Passwords don't match",
  });

function PasswordForm() {
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmNewPassword: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    setSaved(false);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      setSaved(true);
      toast.success("Password changed");
      reset();
    } catch (err) {
      setError(err);
      toast.error("Could not change your password");
    }
  });

  function handleAltBtn() {
    reset({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    setError(null);
    setSaved(false);
  }

  return (
    <FormCard
      id="account-password"
      title="Password"
      icon={<PasswordIcon aria-hidden className="size-5" />}
      tile="bg-orange-lighter text-orange-dark"
      description="You'll need your current password to set a new one."
      hint="Infra Junction checks the new password's rules when you save."
      onSubmit={onSubmit}
      outcome={
        <>
          {saved ? <Saved>Your password has been changed.</Saved> : null}
          {error ? <ErrorSurface error={error} action="change your password" /> : null}
        </>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={handleAltBtn}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Change password
          </Button>
        </>
      }
    >
      <div className="grid gap-5 @2xl:grid-cols-2">
        <Password
          label="Current password"
          id="account-current-password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register("currentPassword")}
        />
        <div aria-hidden className="hidden @2xl:block" />
        <Password
          label="New password"
          id="account-new-password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          {...register("newPassword")}
        />
        <Password
          label="Confirm new password"
          id="account-confirm-new-password"
          autoComplete="new-password"
          error={errors.confirmNewPassword?.message}
          {...register("confirmNewPassword")}
        />
      </div>
    </FormCard>
  );
}

/**
 * MeController's `/me/sessions` (US1, FR-048) — not permission-gated, same as the two forms above:
 * this acts on the caller's own account, and the matrix has nothing to say about reading or
 * revoking yourself. The current session can't be revoked from here — signing out is `signOut()`,
 * not this list.
 */
const SESSION_PREVIEW = 5;

function SessionsSection({ sessions }: { sessions: MySession[] }) {
  const revalidator = useRevalidator();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [showAll, setShowAll] = useState(false);
  // An account signed in from dev and e2e runs can carry hundreds of sessions; the list opens on the
  // current one plus the next few, and "Show all" is one click away. Display order only.
  const ordered = [...sessions.filter((s) => s.current), ...sessions.filter((s) => !s.current)];
  const visible = showAll ? ordered : ordered.slice(0, SESSION_PREVIEW);

  async function handleRevoke(id: string) {
    setError(null);
    setRevokingId(id);
    try {
      await revokeMySession(id);
      toast.success("Session revoked");
      setConfirmId(null);
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not revoke that session");
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <WidgetCard
      rounded="xl"
      headerClassName="items-center"
      title={
        <span id="account-devices" className="scroll-mt-28">
          <CardTitle icon={<DevicesIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
            Signed-in devices
          </CardTitle>
        </span>
      }
      action={
        <Badge variant="flat" color="success" rounded="pill">
          {sessions.length} active
        </Badge>
      }
      description={<Text className="mt-2 text-sm text-gray-500">Sessions currently signed in as you. Revoking one signs that device out immediately.</Text>}
    >
      <div className="mt-5 flex flex-col gap-4">
        {error ? <ErrorSurface error={error} action="revoke that session" /> : null}
        {sessions.length === 0 ? (
          <EmptyState title="No active sessions">Nothing is signed in as you right now.</EmptyState>
        ) : (
          <ul className="flex flex-col gap-3">
            {visible.map((session) => (
              <li
                key={session.id}
                className={cn(
                  "flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 transition-colors",
                  session.current ? "border-green/40 bg-green-lighter/20 dark:bg-green-lighter/10" : "border-muted hover:bg-gray-50 dark:hover:bg-gray-100/40",
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={cn(
                      "flex size-11 shrink-0 items-center justify-center rounded-xl",
                      session.current ? "bg-green-lighter text-green-dark" : "bg-gray-100 text-gray-600",
                    )}
                  >
                    <DevicesIcon aria-hidden className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Text className="font-medium text-gray-900">{session.client ?? "Unknown device"}</Text>
                      {session.current ? (
                        <Badge variant="flat" color="success" rounded="pill" size="sm" className="gap-1.5">
                          <Badge renderAsDot color="success" size="sm" />
                          This device
                        </Badge>
                      ) : null}
                    </div>
                    <Text className="text-sm text-gray-500">
                      <span className="font-mono">{session.sourceIp ?? "IP not recorded"}</span> · last active {formatMoment(session.lastUsedAt)}
                    </Text>
                  </div>
                </div>

                {session.current ? (
                  <Text as="span" className="text-sm text-gray-500" title="You can't revoke the session you're using right now.">
                    Current session
                  </Text>
                ) : confirmId === session.id ? (
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="solid" color="danger" size="sm" isLoading={revokingId === session.id} onClick={() => handleRevoke(session.id)}>
                      Confirm revoke
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setConfirmId(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" color="danger" size="sm" onClick={() => setConfirmId(session.id)}>
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {sessions.length > SESSION_PREVIEW ? (
          <Button type="button" variant="text" size="sm" className="self-center text-green-dark" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show fewer" : `Show all ${sessions.length} devices`}
          </Button>
        ) : null}
      </div>
    </WidgetCard>
  );
}
